import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { FiCalendar, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import { transactionsService } from '../services';
import type { Transaction } from '../types';
import { Card, LoaderComponent } from './ui';
import { todayCv, cvDayKey } from '../utils/tempoCv';

interface DayData {
  day: string;
  shortDay: string;
  date: string;
  total: number;
  count: number;
}

const WeekPaymentsChart: React.FC = () => {
  const [weekData, setWeekData] = useState<DayData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalWeek, setTotalWeek] = useState(0);
  const [weekChange, setWeekChange] = useState<number | null>(null);

  useEffect(() => {
    loadWeekData();
  }, []);

  // Semana civil de CV. Cada dia é representado pela meia-noite UTC desse dia de CV,
  // para que toISOString().split('T')[0] dê o dia certo em qualquer fuso do browser.
  const getWeekDates = () => {
    const [y, m, d] = todayCv().split('-').map(Number);
    const dayOfWeek = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    // Get start of week (Monday)
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go back 6 days
    const startOfWeek = new Date(Date.UTC(y, m - 1, d + diff));
    const endOfWeek = new Date(Date.UTC(y, m - 1, d + diff + 6));

    // Previous week for comparison
    const prevWeekStart = new Date(Date.UTC(y, m - 1, d + diff - 7));
    const prevWeekEnd = new Date(Date.UTC(y, m - 1, d + diff - 1));

    return { startOfWeek, endOfWeek, prevWeekStart, prevWeekEnd };
  };

  const loadWeekData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { startOfWeek, endOfWeek, prevWeekStart, prevWeekEnd } = getWeekDates();

      // Format dates for API
      const formatDate = (date: Date) => date.toISOString().split('T')[0];

      // Fetch current week and previous week transactions in parallel
      const [currentWeekTx, prevWeekTx] = await Promise.all([
        transactionsService.getTransactionsByDateRange(
          formatDate(startOfWeek),
          formatDate(endOfWeek)
        ).catch(() => []),
        transactionsService.getTransactionsByDateRange(
          formatDate(prevWeekStart),
          formatDate(prevWeekEnd)
        ).catch(() => []),
      ]);

      // Initialize week days
      const days: DayData[] = [];
      const dayNames = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
      const fullDayNames = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setUTCDate(startOfWeek.getUTCDate() + i);
        days.push({
          day: fullDayNames[i],
          shortDay: dayNames[i],
          date: formatDate(date),
          total: 0,
          count: 0,
        });
      }

      // Aggregate transactions by day
      currentWeekTx.forEach((tx: Transaction) => {
        const txDate = tx.data || tx.dataRegisto || '';
        const txDateStr = cvDayKey(txDate); // dia de CV do instante, não o dia UTC
        const dayIndex = days.findIndex((d) => d.date === txDateStr);
        if (dayIndex >= 0) {
          const amount = Math.abs(tx.valor || tx.montante || 0);
          days[dayIndex].total += amount;
          days[dayIndex].count += 1;
        }
      });

      // Calculate totals
      const currentTotal = days.reduce((sum, d) => sum + d.total, 0);
      const prevTotal = prevWeekTx.reduce((sum: number, tx: Transaction) => {
        return sum + Math.abs(tx.valor || tx.montante || 0);
      }, 0);

      // Calculate percentage change
      if (prevTotal > 0) {
        const change = ((currentTotal - prevTotal) / prevTotal) * 100;
        setWeekChange(Math.round(change));
      } else if (currentTotal > 0) {
        setWeekChange(100);
      } else {
        setWeekChange(null);
      }

      setWeekData(days);
      setTotalWeek(currentTotal);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount: number): string => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M$`;
    }
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K$`;
    }
    return `${amount.toFixed(0)}$`;
  };

  const formatFullAmount = (amount: number): string => {
    return new Intl.NumberFormat('pt-CV', {
      style: 'decimal',
      minimumFractionDigits: 0,
    }).format(amount) + '$';
  };

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      fontFamily: 'Poppins, sans-serif',
      toolbar: { show: false },
      sparkline: { enabled: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '60%',
        distributed: true,
      },
    },
    colors: weekData.map((d, i) => {
      const [ty, tm, td] = todayCv().split('-').map(Number);
      const today = new Date(Date.UTC(ty, tm - 1, td)).getUTCDay();
      const adjustedToday = today === 0 ? 6 : today - 1; // Convert to Mon=0 format
      return i === adjustedToday ? '#B4BF09' : '#E5E7EB';
    }),
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: weekData.map((d) => d.shortDay),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: {
          colors: '#6B7280',
          fontSize: '11px',
        },
      },
    },
    yaxis: {
      show: false,
    },
    grid: {
      show: false,
      padding: { top: 0, right: 0, bottom: 0, left: 0 },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val: number) => formatFullAmount(val),
      },
    },
  };

  const chartSeries = [
    {
      name: 'Pagamentos',
      data: weekData.map((d) => d.total),
    },
  ];

  return (
    <Card className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-pagali-primary/10 rounded-lg flex items-center justify-center">
            <FiCalendar className="text-pagali-primary" />
          </div>
          <h3 className="text-sm font-semibold text-pagali-gray-800">Esta Semana</h3>
        </div>
        {weekChange !== null && (
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1 ${
              weekChange >= 0
                ? 'bg-green-100 text-green-600'
                : 'bg-red-100 text-red-600'
            }`}
          >
            {weekChange >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
            {Math.abs(weekChange)}%
          </span>
        )}
      </div>

      {/* Total */}
      <p className="text-2xl font-bold text-pagali-gray-800 mb-2">
        {formatAmount(totalWeek)}
      </p>

      {/* Chart */}
      {loading ? (
        <LoaderComponent size="sm" />
      ) : error ? (
        <p className="text-pagali-error text-xs py-4 text-center">{error}</p>
      ) : (
        <div className="-mx-2">
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="bar"
            height={120}
          />
        </div>
      )}
    </Card>
  );
};

export default WeekPaymentsChart;
